import React, { useEffect, useState, useCallback } from 'react';
import classnames from 'classnames';

import { isElementOfType, sanitizeFragments } from '../../utilities/components';

import { Portal } from '../portal/Portal';
import WpBackdrop from '../backdrop/WpBackdrop';
import WpModalHeader from './WpModalHeader';
import WpModalBody from './WpModalBody';

import './WpModal.scss';

export type Size = 'full' | 'lg' | 'md' | 'sm';
const DEFAULT_SIZE = 'md';

export interface Props {
  /**
   * Size of modal
   * @default 'md'
   */
  size?: Size;
  isOpen: boolean;
  children?: React.ReactNode;
  innerClassName?: string;
  onToggle?(): void;
  qaTest?: string;
  dismissOnBackdrop?: boolean;
  hideBackdrop?: boolean;
  animation?: boolean;
  // to be used sparingly - but to allow modals on modals on portals, etc
  zIndex?: number;
}

const classNamePrefix = 'WpModal';
const bodyClassNamePrefix = 'Body--WpModal-opened';

const WpModal: React.FC<Props> = ({
  size = DEFAULT_SIZE,
  isOpen = false,
  children,
  onToggle,
  qaTest,
  innerClassName,
  dismissOnBackdrop = true,
  hideBackdrop = false,
  animation = true,
  zIndex,
}: Props) => {
  const [isModalRender, setIsModalRender] = useState(!animation);

  const classes = classnames(classNamePrefix, `${classNamePrefix}--size-${size}`, {
    [`${classNamePrefix}--open`]: isModalRender,
  });

  const contentClasses = classnames(
    `${classNamePrefix}__content`,
    `${classNamePrefix}__content--size-${size}`,
    innerClassName
  );

  const removeStylesFromBody = useCallback(() => {
    document.body.classList.remove(bodyClassNamePrefix);
    document.body.style.paddingRight = null;
  }, []);

  const addStylesToBody = useCallback(() => {
    const prevWidth = document.body.clientWidth;
    document.body.classList.add(bodyClassNamePrefix);
    const currentWidth = document.body.clientWidth;
    document.body.style.paddingRight = `${currentWidth - prevWidth}px`;
  }, []);

  useEffect(() => {
    if (animation) setTimeout(() => setIsModalRender(isOpen));
    if (isOpen) addStylesToBody();
    else removeStylesFromBody();

    return removeStylesFromBody;
  }, [isOpen]);

  if (!isOpen) return null;

  const sanitizedChildren = children && sanitizeFragments(children);

  return (
    <Portal idPrefix="modal">
      <div className={classes} data-test={qaTest} style={{ zIndex }}>
        <div className={contentClasses}>
          {React.Children.map(sanitizedChildren, (child: React.ReactElement<any>) => {
            if (child && isElementOfType(child, WpModalHeader)) {
              return React.cloneElement(child, { onToggle, qaTest });
            }
            if (child && isElementOfType(child, WpModalBody)) {
              return React.cloneElement(child, { size });
            }
            return child;
          })}
        </div>
        {!hideBackdrop && <WpBackdrop animation={animation} onClick={dismissOnBackdrop && onToggle} />}
      </div>
    </Portal>
  );
};

export default WpModal;
